import { Dictionary } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import { User } from 'api/models';
import {
  ConvertAllocationsProps,
  DeleteAllocationsProps,
  ResourceType,
} from 'api/resource-management/types';
import { StatusFieldOption } from 'api/tasks';
import { ProjectAllocation } from 'components/organisms/resource-management/scheduler-config/columns/cells/allocation';
import { AssignProject } from 'components/organisms/resource-management/scheduler-config/columns/cells/assign-project';
import { ExpandableRoleForUsersView } from 'components/organisms/resource-management/scheduler-config/columns/cells/expandable-role-for-users-view';
import { ProjectCellForUsersView } from 'components/organisms/resource-management/scheduler-config/columns/cells/project-for-users-view';
import { TeamCountCell } from 'components/organisms/resource-management/scheduler-config/columns/cells/team-count';
import { TeamCellForUsersView } from 'components/organisms/resource-management/scheduler-config/columns/cells/team-for-users-view';
import { TeamHeaderForUsersView } from 'components/organisms/resource-management/scheduler-config/columns/cells/team-header-for-users-view';
import { UserCellForUsersView } from 'components/organisms/resource-management/scheduler-config/columns/cells/user-for-users-view';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { defaultFormat } from 'utils/date-field/formatter';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { SchedulerResourceType } from '../../resource-types';
import { ProjectsWithMembers, SchedulerReference, TeamLite } from '../../types';
import { UserRemovedFromProjectParams } from '../listeners';
import { CompanyCellForUsersView } from './cells/company-for-users-view';
import { PartnerHeaderForUsersView } from './cells/partner-header-for-users-view';
import { ColumnConfig } from './types';

interface Props {
  schedulerRef: SchedulerReference;
  projectStatusLookup: Dictionary<StatusFieldOption>;
  projectLookup: Dictionary<ProjectsWithMembers>;
  userLookup: Dictionary<User>;
  softAllocationsEnabled: boolean;
  onFindMember: (props: TeamLite) => void;
  onEditTeam: (teamId: number) => void;
  deleteAllocationHandler: (params: DeleteAllocationsProps) => void;
  convertAllocationHandler: (params: ConvertAllocationsProps) => void;
}

export const useColumnsConfigForGroupByUser = ({
  schedulerRef,
  projectLookup,
  userLookup,
  projectStatusLookup,
  softAllocationsEnabled,
  onFindMember,
  onEditTeam,
  deleteAllocationHandler,
  convertAllocationHandler,
}: Props): ColumnConfig[] => {
  const isCollaborator = useIsCurrentUserCollaborator();
  const teamMembersTitle = useTranslation('team_members');
  const allocationColumnTitle = useTranslation(
    'resource_management.columns.allocation',
  );
  const title = !isCollaborator ? teamMembersTitle : '';
  const projectOptions = useMemo(
    () =>
      Object.values(projectLookup)
        .map((project) => ({
          label: project.projectName,
          value: project.projectId,
          imageUrl: project.customer.companyLogoUrl,
          extra: {
            partners: (project.partners ?? []).map(
              ({ companyId }) => companyId,
            ),
          },
        }))
        .sort(({ label: a }, { label: b }) => sortByText(a, b)),
    [projectLookup],
  );
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );

  // TODO: To try and use the more actions hook here
  return useMemo(
    () => [
      {
        text: title,
        width: 360,
        type: 'tree',
        renderer: (arg) => {
          try {
            const { record, size, cellElement } = arg;
            const { type, autoAllocation } = record;

            record.cls = `${type} loading-skeleton${
              autoAllocation === true ? ' auto-allocate' : ''
            }`;

            switch (type as SchedulerResourceType) {
              case SchedulerResourceType.TeamHeader:
                size.height = 48;
                cellElement.classList.add('full-width');

                return (
                  <TeamHeaderForUsersView
                    onWrapperClick={() => {
                      schedulerRef.current.instance.toggleCollapse(record.id);
                    }}
                  />
                );
              case SchedulerResourceType.PartnerHeader:
                size.height = 48;
                cellElement.classList.add('full-width');

                return (
                  <PartnerHeaderForUsersView
                    onWrapperClick={() => {
                      schedulerRef.current.instance.toggleCollapse(record.id);
                    }}
                  />
                );
              case SchedulerResourceType.ExpandableRole:
                size.height = 48;
                cellElement.classList.add('no-right-border');

                return (
                  <ExpandableRoleForUsersView
                    roleName={record.roleName}
                    onWrapperClick={() => {
                      schedulerRef.current.instance.toggleCollapse(record.id);
                    }}
                  />
                );
              case SchedulerResourceType.Team:
                return (
                  <TeamCellForUsersView
                    projectName={record.projectName}
                    customer={record.customer}
                    teamName={record.teamName}
                    projectId={record.projectId}
                    teamId={record.teamId}
                    projectStatus={projectStatusLookup[record.projectId]}
                    softAllocationsEnabled={softAllocationsEnabled}
                    onFindMember={() => {
                      onFindMember({
                        projectId: record.projectId,
                        teamId: record.teamId,
                        teamName: record.teamName,
                        roleId: record.roleId,
                      });
                    }}
                    autoAllocation={autoAllocation}
                    onEditTeam={() => onEditTeam(record.teamId)}
                    onDeleteAllocations={() => {
                      deleteAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.Role,
                          roleId: record.roleId,
                          teamId: record.teamId,
                        },
                      });
                    }}
                    onConvertAllocations={() => {
                      convertAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.Role,
                          roleId: record.roleId,
                          teamId: record.teamId,
                        },
                      });
                    }}
                    onTeamRemoval={(projectId, teamId) => {
                      schedulerRef.current.instance.trigger(
                        'onTeamRemovedFromProject',
                        {
                          projectId,
                          teamId,
                        },
                      );
                    }}
                  />
                );
              case SchedulerResourceType.Role:
                size.height = 32;
                cellElement.classList.add('full-width');

                return record.roleName;
              case SchedulerResourceType.User:
                cellElement.classList.add('full-width');

                return (
                  <UserCellForUsersView
                    firstName={record.firstName}
                    lastName={record.lastName}
                    photoUrl={record.photoUrl}
                    role={record.role}
                    onWrapperClick={() => {
                      schedulerRef.current.instance.toggleCollapse(record.id);
                    }}
                  />
                );
              case SchedulerResourceType.Project:
                if (record.referenceTeam == null) {
                  size.height = 48;
                }

                return (
                  <ProjectCellForUsersView
                    projectId={record.projectId}
                    projectName={record.projectName}
                    customer={record.customer}
                    userId={record.userId}
                    fullName={fullName(userLookup[record.userId])}
                    projectStatus={projectStatusLookup[record.projectId]}
                    startDate={defaultFormat(moment(record.startDate))}
                    isUserProjectOwner={record.isUserProjectOwner}
                    roleId={record.roleId}
                    referenceTeam={record.referenceTeam}
                    softAllocationsEnabled={softAllocationsEnabled}
                    removeUserFromProjectInUI={(
                      params: UserRemovedFromProjectParams,
                    ): void =>
                      schedulerRef.current.instance.trigger(
                        'onUserRemovedFromProject',
                        params,
                      )
                    }
                    goToStartOfProject={() => {
                      schedulerRef.current.instance.trigger(
                        'onToStartOfProject',
                        {
                          projectId: record.projectId,
                        },
                      );
                    }}
                    autoAllocation={autoAllocation}
                    onDeleteAllocations={() => {
                      deleteAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.User,
                          userId: record.userId,
                          ...(record.referenceTeam
                            ? {
                                roleId: record.roleId,
                                referenceTeamId: record.referenceTeam.teamId,
                              }
                            : {}),
                        },
                      });
                    }}
                    onConvertAllocations={() => {
                      convertAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.User,
                          userId: record.userId,
                          ...(record.referenceTeam
                            ? {
                                roleId: record.roleId,
                                referenceTeamId: record.referenceTeam.teamId,
                              }
                            : {}),
                        },
                      });
                    }}
                  />
                );
              case SchedulerResourceType.AssignProject:
                size.height = 48;
                cellElement.classList.add('full-width');

                return (
                  <AssignProject
                    projectOptions={projectOptions}
                    alreadyAssignedProjectIds={record.alreadyAssignedProjectIds}
                    userId={record.userId}
                    userType={record.userType}
                    company={record.company}
                    onProjectAssign={(userId, projectId) =>
                      schedulerRef.current.instance.trigger(
                        'onUserAssignedToProject',
                        {
                          projectId,
                          userId,
                        },
                      )
                    }
                  />
                );
              case SchedulerResourceType.Company:
                size.height = 48;
                cellElement.classList.add('full-width');

                return (
                  <CompanyCellForUsersView
                    onWrapperClick={() => {
                      schedulerRef.current.instance.toggleCollapse(record.id);
                    }}
                    companyId={record.get('companyId')}
                    companyName={record.get('companyName')}
                    companyLogoUrl={record.get('companyLogoUrl')}
                  />
                );

              default:
                return '';
            }
          } catch (e) {
            logger.log('Error in rendering columns for people', e);

            return <></>; // ? Can we handle it better ?
          }
        },
      },
      {
        text: allocationColumnTitle,
        width: 104,
        id: 'allocatedHours',
        renderer: (arg) => {
          try {
            const { record } = arg;
            const { type, autoAllocation } = record;

            switch (type as SchedulerResourceType) {
              case SchedulerResourceType.Team:
              case SchedulerResourceType.Project:
                return (
                  <ProjectAllocation
                    budgetedHours={record.budgetedHours}
                    totalAllocation={record.totalAllocation}
                    isTimesheetFeatureEnabled={isTimesheetFeatureEnabled}
                    showDetailedView={false}
                    autoAllocate={autoAllocation}
                    dataLoading={record.dataLoading}
                  />
                );
              case SchedulerResourceType.ExpandableRole:
                return <TeamCountCell teamCount={record.descendantCount} />;
              case SchedulerResourceType.Role:
              case SchedulerResourceType.User:
              default:
                return '';
            }
          } catch (e) {
            logger.log('Error in rendering allocation column', e);

            return <></>; // ? Can we handle it better ?
          }
        },
      },
    ],
    [
      title,
      allocationColumnTitle,
      projectStatusLookup,
      softAllocationsEnabled,
      userLookup,
      projectOptions,
      schedulerRef,
      onFindMember,
      onEditTeam,
      deleteAllocationHandler,
      convertAllocationHandler,
      isTimesheetFeatureEnabled,
    ],
  );
};
